import { useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { toast } from "@/hooks/use-toast";
import { useFeature } from "@/lib/early-access";
import { isChecklistOpen, readChecklist } from "@/lib/guided/checklist";
import { isFirstRunPending, shouldShowOnboarding } from "@/lib/onboarding";
import {
  shouldAutoOpenWhatsNew,
  type WhatsNewFeature,
  whatsNewFeatures,
} from "@/lib/whats-new";
import { useAuthStore } from "@/store/auth.store";
import { useGuidedChecklistStore } from "@/store/guidedChecklist.store";
import { useWhatsNewStore } from "@/store/whatsNew.store";
import { findLatestVisitId } from "./actions";
import { WhatsNewDialog } from "./WhatsNewDialog";

// Lets the page settle (and the welcome finish closing) before it appears.
export const WHATS_NEW_OPEN_DELAY_MS = 700;

// Opens "What's new" once after an update (queued after the first-time
// welcome), reopens it from the menu, and runs the cards' "Try it".
export const WhatsNewHost = (): JSX.Element | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useAuthStore((state) => state.userData?.public_id);
  const open = useWhatsNewStore((state) => state.open);
  const welcomeOpen = useWhatsNewStore((state) => state.welcomeOpen);
  // Re-read "seen" whenever it changes.
  useWhatsNewStore((state) => state.seenVersion);
  const { show, hide, markSeen, isSeen, setIntent } =
    useWhatsNewStore.getState();
  const features = useMemo(() => whatsNewFeatures(), []);
  // Guided first visit: the "first steps" checklist comes first; What's new
  // waits until it is done or dismissed.
  // Re-read whenever the list changes.
  useGuidedChecklistStore((state) => state.state);
  const guided = useFeature("guided-first-visit");
  const checklistOpen = guided && isChecklistOpen(readChecklist(account));

  const autoOpen = shouldAutoOpenWhatsNew({
    enabled: AppConfig.enableWhatsNew,
    account,
    seen: isSeen(account),
    welcomeOpen,
    welcomeDue: shouldShowOnboarding(account),
    firstRunPending: isFirstRunPending(account) || checklistOpen,
    hasFeatures: features.length > 0,
  });

  useEffect(() => {
    if (!autoOpen || open) return;
    const timer = setTimeout(show, WHATS_NEW_OPEN_DELAY_MS);
    return (): void => {
      clearTimeout(timer);
    };
  }, [autoOpen, open, show]);

  const close = useCallback((): void => {
    hide();
    markSeen(account);
  }, [account, hide, markSeen]);

  const tryFeature = useCallback(
    async (feature: WhatsNewFeature): Promise<void> => {
      close();
      const { action } = feature;
      if (action.kind === "route") {
        await navigate({ to: action.to, hash: action.hash });
        return;
      }
      if (action.kind === "newVisit") {
        await navigate({ to: "/visits", search: { openNewVisit: true } });
        return;
      }
      const visitPublicId = await findLatestVisitId().catch(() => null);
      if (visitPublicId === null) {
        toast({ description: t("whatsNew.needsVisit") });
        await navigate({ to: "/visits", search: { openNewVisit: true } });
        return;
      }
      setIntent(action.focus);
      await navigate({
        to: "/visits/$visitPublicId",
        params: { visitPublicId: String(visitPublicId) },
      });
    },
    [close, navigate, setIntent, t],
  );

  if (!AppConfig.enableWhatsNew || features.length === 0) return null;

  return (
    <WhatsNewDialog
      features={features}
      open={open}
      onOpenChange={(next) => {
        if (next) show();
        else close();
      }}
      onTry={(feature) => {
        void tryFeature(feature);
      }}
    />
  );
};
